import { Component, EventEmitter, HostBinding, Input, Output } from "@angular/core";
import { VngButtonComponent, VngIconComponent } from "@vengreso/angular-components";

/**
 * The corner "X" dismiss button shared by the app's dialogs — the
 * `<button vng-button variant="ghost" size="sm" tabindex="-1">` +
 * `<vng-icon name="close">` pair that was byte-identical across the plan/
 * settings/onboarding dialogs and the FlyShare dialogs.
 *
 * POSITIONING STAYS AT THE CALL SITE: every host keeps its own
 * `class="close-button"` on the `<app-dialog-close-button>` element itself,
 * so the host-scoped `.close-button { float: right; ... }` rules (and
 * warning-dialog's `::ng-deep .icon-outside .close-button` override) keep
 * applying exactly as before — just to this wrapper element instead of the
 * raw button. The `:host` is `inline-flex` like the button it wraps, so the
 * floated/inline box geometry is unchanged.
 *
 * Two flavors exist:
 * - default (plan/settings dialogs): 20px icon, `#1c1b1f`.
 * - share dialogs (fly-cut-share/-invite/-notify, share-history):
 *   `[size]="30"` and `color="black"`.
 *
 * Plus an opt-in `[compact]` box mode — see the @Input.
 */
@Component({
  selector: "app-dialog-close-button",
  standalone: true,
  imports: [VngButtonComponent, VngIconComponent],
  template: `
    <button vng-button variant="ghost" size="sm" tabindex="-1" [style.color]="color" (click)="closed.emit()">
      <vng-icon name="close" [outline]="false" [size]="size" [style.color]="color"></vng-icon>
    </button>
  `,
  styles: [
    ":host { display: inline-flex; }",
    /* V4-02: `[compact]` — the glyph box IS the button box.
       The library's geometry lives in tokens (`min-width:
       var(--vng-btn-min-width)` on the host, `height/padding:
       var(--vng-btn-height-sm)/var(--vng-btn-padding-sm)` on
       `:host(.vng-btn--sm)`), so overriding the three tokens on OUR host — they
       inherit down onto the button — collapses the box with no specificity
       fight against the library's own `:host(...)` rules and no template
       change. */
    `:host(.app-dialog-close-button--compact) {
       --vng-btn-min-width: 0;
       --vng-btn-padding-sm: 0;
       --vng-btn-height-sm: auto;
     }`,
  ],
})
export class DialogCloseButtonComponent {
  /**
   * Icon size in px. Share dialogs pass 30.
   *
   * R8: the default used to be `null`, which binds no font-size onto the
   * icon (vng-icon's HostBinding skips null), so the ligature fell back to
   * the button's `size="sm"` font-size (`--vng-font-size-xs`, ~12px) and
   * rendered as a ~11px glyph across every dialog that did not pass a size.
   */
  @Input() size: number = 20;

  /** Icon (and button) color. Share dialogs pass "black". */
  @Input() color = "#1c1b1f";

  /**
   * V4-02: opt in to a square, glyph-sized box.
   *
   * `vng-button size="sm"` is a 64x30 pill (`--vng-btn-min-width: 64px` +
   * `--vng-btn-padding-sm: 0 8px`), which centres the 20px glyph 22px inside
   * its own box and 5px below its top — so every host that positions this
   * button by its corner ends up with the GLYPH 22px/5px further in than the
   * offset it wrote, i.e. the ~42px right inset V4-02 measured on eight dialogs
   * against master's 19-25px. Rounds 3-5 each undid that box by hand at the
   * call site (`min-width: 0; padding: 0` in statistics / version-history /
   * share-history's stylesheets); this is the same collapse, once, as an
   * opt-in.
   *
   * Deliberately opt-IN: consumers whose inset is already correct (the shared
   * ConfirmDialogComponent, unsaved-changes, warning-dialog, compare-plans, the
   * payment-method confirmations) are tuned around the wide box and must not
   * move.
   */
  @Input() compact = false;

  @HostBinding("class.app-dialog-close-button--compact")
  get isCompact(): boolean {
    return this.compact;
  }

  /** Emitted on click — hosts call their own `close()`/`cancel()`. */
  @Output() closed = new EventEmitter<void>();
}
